/**
 * Format a number as currency, returning '-' for null/undefined/zero.
 * Used for schedule line items where zero means "not applicable".
 */
export const formatOattAmount = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '-';
  return Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format a number as currency, always showing 2 decimal places.
 * Used for charges and totals that must always display a value.
 */
export const formatOattAmountAlways = (v: number | null | undefined): string => {
  return Number(v ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format an integer (no decimals). Used for MW Granted.
 * Returns '-' for null/undefined.
 */
export const formatOattInt = (v: number | null | undefined): string => {
  if (v == null) return '-';
  return Number(v).toLocaleString('en-US');
};

/**
 * Derives a "Month YYYY" display label from a billing_period string
 * (e.g. "2025-05" or "2025-05-01"). Falls back to the provided fallback
 * string if billing_period is absent or unparseable.
 */
export const derivedMonthLabel = (billingPeriod: string | null, fallback: string): string => {
  if (!billingPeriod) return fallback;
  const match = billingPeriod.match(/^(\d{4})-(\d{2})/);
  if (match) {
    const date = new Date(parseInt(match[1]), parseInt(match[2]) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  return billingPeriod;
};

/**
 * Generate a sanitized PDF filename for an OATT invoice.
 */
export const generateOattFilename = (
  customerCode: string,
  billingPeriod: string | null,
  monthLabel: string,
  isRevision: boolean | null,
  revisionNo: number | null,
): string => {
  const displayMonthLabel = derivedMonthLabel(billingPeriod, monthLabel);
  const [month, year] = displayMonthLabel.split(' ');
  const code = (customerCode ?? 'UNKNOWN').replace(/[^a-z0-9]/gi, '_');

  return isRevision
    ? `Revised_OATT_${code}_${month}_${year}_Bill_${revisionNo}.pdf`
    : `OATT_${code}_${month}_${year}_Bill.pdf`;
};
